import { css } from '@emotion/react';
import { UseComboboxPropGetters } from 'downshift';
import { FC, useEffect, useRef } from 'react';
import { InterpreterFrom } from 'xstate';

import { AddressFormValidation } from 'api/api';

import { Icon } from '~src/commons/components';
import { InputClearIconShell } from '~src/commons/components/text-input/input-clear-icon-shell';
import { useTranslations } from '~src/commons/utils/translations/translate';

import { StandardInput } from '~address-form/inputs/standard-input';
import { useValidationRules } from '~address-form/validation/field-validation';

import { PlaceQueryItem } from '../../types';
import { isStreetNumberMissing } from './autocomplete-helpers';
import { AutocompleteMachine } from './machine';

export const AutocompleteInput: FC<{
  getInputProps: UseComboboxPropGetters<PlaceQueryItem>['getInputProps'];
  machineService: InterpreterFrom<AutocompleteMachine>;
  validationConfig: AddressFormValidation;
  warning?: boolean;
  optionalStreetNumber?: boolean;
}> = ({ getInputProps, machineService, validationConfig, warning, optionalStreetNumber }) => {
  const { tICU } = useTranslations();
  const validationRules = useValidationRules('address_lines', validationConfig);

  const { context, matches } = machineService.getSnapshot();
  const { send: sendEventToMachine } = machineService;

  const ref = useRef<HTMLInputElement>(null);
  const prevValue = useRef<string>(context.inputValue);

  useEffect(() => {
    machineService.onEvent(event => {
      if (event.type === 'CLEAR_EVERYTHING') {
        ref.current?.blur();
      }
    });
  }, []);

  return (
    <>
      <div>
        <StandardInput
          name="address_lines"
          css={styles.input(matches('requestingPlaceDetails'))}
          ref={ref}
          warning={warning}
          {...getInputProps({
            refKey: 'ref',
          })}
          label={tICU('widget.address_form.ADDRESS_LINES_PLACEHOLDER')}
          value={context.inputValue}
          onChange={e => {
            const { value } = e.target;
            /*
             * there's a case when user fills the field via browser autofill
             * and the input won't be focused
             */
            const diff = value.length - prevValue.current.length;
            if (diff > 1 && !matches('focused')) {
              sendEventToMachine('FOCUS');
              sendEventToMachine('KEYSTROKE', { query: value });
              sendEventToMachine('BLUR');
            } else {
              sendEventToMachine('KEYSTROKE', { query: value });
            }
            prevValue.current = value;
          }}
          onFocus={() => sendEventToMachine('FOCUS')}
          rightElement={({ setFocused }) =>
            context.inputValue &&
            matches('focused') && (
              <InputClearIconShell
                color="darkGrey"
                width={16}
                height={13}
                onClick={() => {
                  sendEventToMachine('CLEAR_INPUT');
                  setFocused(false);
                }}
              />
            )
          }
          dataTest="street-and-number-input"
          validationRules={{
            ...validationRules,
            validate: {
              apartmentNumberInAddressLine: value => {
                return (
                  optionalStreetNumber ||
                  !value ||
                  !isStreetNumberMissing(value) ||
                  tICU('widget.delivery_address.STREET_NUMBER_REQUIRED_VALIDATION')
                );
              },
              ...validationRules.validate,
            },
          }}
        />
      </div>

      {(matches('focused.loadingPlaces') || matches('requestingPlaceDetails')) && (
        <Icon type="inlineLoader" css={styles.spinner} />
      )}
    </>
  );
};

AutocompleteInput.displayName = 'AutocompleteInput';

const styles = {
  input: (disabled: boolean) => ({
    ...(disabled && { pointerEvents: 'none' }),
  }),
  clearValueButton: css({
    position: 'absolute',
    right: '16px',
    cursor: 'pointer',
    top: '23px',
  }),
  spinner: css({
    position: 'absolute',
    right: '43px',
    width: '15px',
    top: '21px',
    height: '15px',
  }),
};
